import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import {
  applyAppearance,
  clampScale,
  defaultAppearance,
  parseHex,
  readStoredAppearance,
  storeAppearance,
  type Appearance,
  type ThemeColorRole,
  type ThemeFontId,
  type ThemeTextRole,
} from './appearance';

type AppearanceContextValue = {
  appearance: Appearance;
  setFont: (font: ThemeFontId) => void;
  setTypeScale: (role: ThemeTextRole, scale: number) => void;
  setColor: (role: ThemeColorRole, hex: string | null) => void;
  resetText: () => void;
  resetColors: () => void;
  resetFont: () => void;
  resetAll: () => void;
};

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export const AppearanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [appearance, setAppearance] = useState<Appearance>(() => readStoredAppearance());

  useLayoutEffect(() => {
    applyAppearance(appearance);
    storeAppearance(appearance);
  }, [appearance]);

  const setFont = useCallback((font: ThemeFontId) => {
    setAppearance((current) => (current.font === font ? current : { ...current, font }));
  }, []);

  const setTypeScale = useCallback((role: ThemeTextRole, scale: number) => {
    const next = clampScale(scale);
    setAppearance((current) => {
      if (current.typeScale[role] === next) return current;
      return { ...current, typeScale: { ...current.typeScale, [role]: next } };
    });
  }, []);

  const setColor = useCallback((role: ThemeColorRole, hex: string | null) => {
    const next = hex ? parseHex(hex) : null;
    setAppearance((current) => {
      if (current.colors[role] === next) return current;
      return { ...current, colors: { ...current.colors, [role]: next } };
    });
  }, []);

  const resetText = useCallback(() => {
    setAppearance((current) => ({ ...current, typeScale: defaultAppearance().typeScale }));
  }, []);

  const resetColors = useCallback(() => {
    setAppearance((current) => ({ ...current, colors: defaultAppearance().colors }));
  }, []);

  const resetFont = useCallback(() => {
    setAppearance((current) => (current.font === 'inter' ? current : { ...current, font: 'inter' }));
  }, []);

  const resetAll = useCallback(() => {
    setAppearance(defaultAppearance());
  }, []);

  const value = useMemo(
    () => ({ appearance, setFont, setTypeScale, setColor, resetText, resetColors, resetFont, resetAll }),
    [appearance, setFont, setTypeScale, setColor, resetText, resetColors, resetFont, resetAll]
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
};

export function useAppearance(): AppearanceContextValue {
  const value = useContext(AppearanceContext);
  if (!value) {
    throw new Error('useAppearance must be used within AppearanceProvider');
  }
  return value;
}
